/**
 * SSE React Hook
 * 使用 @microsoft/fetch-event-source 支持 JWT Token 认证
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { API_BASE_URLS, TokenManager } from '@/shared/services/api'

// ==================== 类型定义 ====================

export interface SSEEventData {
  event?: string
  session_id?: string
  entities_count?: number
  relationships_count?: number
  progress?: number
  message?: string
  error?: string
  status?: string
  timestamp?: string
}

interface UseSSEOptions {
  autoConnect?: boolean
  onExtractionStarted?: (data: SSEEventData) => void
  onExtractionProgress?: (data: SSEEventData) => void
  onExtractionCompleted?: (data: SSEEventData) => void
  onExtractionFailed?: (data: SSEEventData) => void
  onSessionStatus?: (data: SSEEventData) => void
  onConnected?: (data: SSEEventData) => void
}

interface UseSSEReturn {
  isConnected: boolean
  connect: () => void
  disconnect: () => void
  lastExtraction: SSEEventData | null
  isExtracting: boolean
  extractionProgress: number
}

// ==================== SSE 客户端类 ====================

type EventType = 'connected' | 'extraction_started' | 'extraction_progress' | 'extraction_completed' | 'extraction_failed' | 'session_status' | 'heartbeat'
type EventHandler = (data: SSEEventData) => void

class SSEClient {
  private userId: string
  private abortController: AbortController | null = null
  private handlers: Map<EventType, Set<EventHandler>> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private isConnecting = false

  constructor(userId: string) {
    this.userId = userId
  }

  async connect(): Promise<void> {
    if (this.isConnecting) {
      return
    }

    this.disconnect()
    this.isConnecting = true
    this.abortController = new AbortController()

    // 获取 Token（使用 TokenManager.getToken() 确保 key 一致）
    const token = TokenManager.getToken()
    if (!token) {
      console.error('[SSE] No access token found')
      this.isConnecting = false
      return
    }

    // SSE 连接使用 Memory Admin 服务（端口 8003）
    const url = `${API_BASE_URLS.memoryAdmin}/sse/connect?user_id=${this.userId}`

    try {
      await fetchEventSource(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: this.abortController.signal,

        onopen: async (response) => {
          if (response.ok) {
            console.log('[SSE] Connected')
            this.reconnectAttempts = 0
            this.emit('connected', { message: 'Connected' })
          } else {
            console.error('[SSE] Connection failed:', response.status, response.statusText)
            throw new Error(`SSE connection failed: ${response.status}`)
          }
        },

        onmessage: (event) => {
          try {
            // 解析事件数据
            const data = JSON.parse(event.data) as SSEEventData
            // 事件类型可能在 event.event 或 data.event 中
            const eventType = (event.event || data.event || 'heartbeat') as EventType
            
            console.log('[SSE] Event received:', eventType, data)
            this.emit(eventType, data)
          } catch (error) {
            console.error('[SSE] Parse error:', error, 'Raw data:', event.data)
          }
        },

        onerror: (error) => {
          console.error('[SSE] Error:', error)
          this.isConnecting = false

          // 重连逻辑
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++
            const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)
            console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
            setTimeout(() => this.connect(), delay)
          } else {
            console.error('[SSE] Max reconnect attempts reached')
          }

          // 不抛出错误，让 fetchEventSource 继续重试
          // throw error
        },

        onclose: () => {
          console.log('[SSE] Connection closed')
          this.isConnecting = false
        },
      })
    } catch (error) {
      console.error('[SSE] Connection error:', error)
      this.isConnecting = false
    }
  }

  disconnect(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.isConnecting = false
  }

  on(event: EventType, handler: EventHandler): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set())
    }
    this.handlers.get(event)!.add(handler)
  }

  off(event: EventType, handler: EventHandler): void {
    this.handlers.get(event)?.delete(handler)
  }

  removeAllListeners(): void {
    this.handlers.clear()
  }

  private emit(event: EventType, data: SSEEventData): void {
    this.handlers.get(event)?.forEach(handler => handler(data))
  }
}

// 全局 SSE 客户端实例
let globalSSEClient: SSEClient | null = null

export function disconnectSSE(): void {
  if (globalSSEClient) {
    globalSSEClient.removeAllListeners()
    globalSSEClient.disconnect()
    globalSSEClient = null
  }
}

// ==================== Hook ====================

export function useSSE(userId: string | null, options: UseSSEOptions = {}): UseSSEReturn {
  const {
    autoConnect = true,
    onExtractionStarted,
    onExtractionProgress,
    onExtractionCompleted,
    onExtractionFailed,
    onSessionStatus,
    onConnected,
  } = options

  const clientRef = useRef<SSEClient | null>(null)
  const connectedRef = useRef(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractionProgress, setExtractionProgress] = useState(0)
  const [lastExtraction, setLastExtraction] = useState<SSEEventData | null>(null)

  // 保存回调的 ref
  const callbacksRef = useRef({
    onExtractionStarted,
    onExtractionProgress,
    onExtractionCompleted,
    onExtractionFailed,
    onSessionStatus,
    onConnected,
  })

  // 更新回调 ref
  useEffect(() => {
    callbacksRef.current = {
      onExtractionStarted,
      onExtractionProgress,
      onExtractionCompleted,
      onExtractionFailed,
      onSessionStatus,
      onConnected,
    }
  })

  // 连接
  const connect = useCallback(() => {
    if (!userId || connectedRef.current) return
    
    connectedRef.current = true
    const client = new SSEClient(userId)
    clientRef.current = client
    globalSSEClient = client

    // 注册事件处理
    client.on('connected', (data) => {
      setIsConnected(true)
      callbacksRef.current.onConnected?.(data)
    })

    client.on('extraction_started', (data) => {
      setIsExtracting(true)
      setExtractionProgress(0)
      callbacksRef.current.onExtractionStarted?.(data)
    })

    client.on('extraction_progress', (data) => {
      if (data.progress !== undefined) {
        setExtractionProgress(data.progress)
      }
      callbacksRef.current.onExtractionProgress?.(data)
    })

    client.on('extraction_completed', (data) => {
      setIsExtracting(false)
      setExtractionProgress(100)
      setLastExtraction(data)
      callbacksRef.current.onExtractionCompleted?.(data)
    })

    client.on('extraction_failed', (data) => {
      setIsExtracting(false)
      setExtractionProgress(0)
      setLastExtraction(data)
      callbacksRef.current.onExtractionFailed?.(data)
    })

    client.on('session_status', (data) => {
      callbacksRef.current.onSessionStatus?.(data)
    })

    client.connect()
  }, [userId])

  // 断开
  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.removeAllListeners()
      clientRef.current.disconnect()
      clientRef.current = null
    }
    if (globalSSEClient === clientRef.current) {
      globalSSEClient = null
    }
    connectedRef.current = false
    setIsConnected(false)
  }, [])

  // 自动连接/断开
  useEffect(() => {
    if (autoConnect && userId) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [userId, autoConnect, connect, disconnect])

  return {
    isConnected,
    connect,
    disconnect,
    lastExtraction,
    isExtracting,
    extractionProgress,
  }
}
